import { ModalBladeProps } from 'components/layouts/ContentLayout'
import { GS_ROLE, GS_ROLE_INFO } from 'utils/constants'
import { useAppDispatch, useAppSelector } from 'utils/custom-hooks/useTypedReduxStore'
import { selectConfigureRoleBladeProps } from 'state/ui/selectors'
import React, { useEffect, useMemo, useState } from 'react'
import BasicDataGridV2 from 'components/generic/datagrid/BasicDataGridV2'
import { useTheme } from '@mui/styles'
import { Box, Button, Checkbox, Divider, Stack, Typography } from '@mui/material_modern'
import FormModalHeading from 'components/generic/modals/form-modal/FormModalHeading'
import { AdminRbacSettings, RbacSettingList } from 'gen/rtk-openapi'
import { useGetSettingsQuery, usePostSettingsRbacMutation } from 'services/settings'
import { setSnackBar } from 'state/ui/index'
import { defaultAdminSettings } from 'components/containers/settings/utils/defaultAdminSettings'
import {
  mapApiModelToGridModel,
  mapGridModelToApiModel,
  rowUpdateFunction,
} from 'components/containers/settings/roles-and-permissions/RolesAndPermissionsHelpers'

export interface ConfigureRoleBladeProps {
  roleId: GS_ROLE
}
// needed for material ui data grid tree view
export type RbacSettingWithPath = RbacSettingList[number] & { path: string[]; indeterminate?: boolean }

const ConfigureRoleBlade: React.FC<ModalBladeProps> = ({ onFormStateChange, onHideModal }) => {
  // eslint-disable-next-line
  useEffect(() => onFormStateChange(false), []) // alert parent that form is clean
  const theme = useTheme()
  const dispatch = useAppDispatch()
  const { roleId } = useAppSelector(selectConfigureRoleBladeProps)
  const {
    variables: { appRouteAssets, appRouteLoadBoard },
  } = useAppSelector(state => state.featureFlags || {})
  const [allRbacSettings, setAllRbacSettings] = useState<AdminRbacSettings>(defaultAdminSettings.rbac)
  const [rbacSettingsForRole, setRbacSettingsForRole] = useState<RbacSettingWithPath[]>([])
  const [selectionDirtied, setSelectionDirtied] = useState(false)

  // RTK query and mutation hooks
  const { data: settingsData, isLoading: isLoadingSettingsData } = useGetSettingsQuery({})
  const [postRbacSetting, { isLoading: isLoadingUpdateSetting }] = usePostSettingsRbacMutation()

  const bladeDataLoading = isLoadingSettingsData || isLoadingUpdateSetting

  //? Handle checkbox toggle
  const handleCheckboxChange = (id: string, checked: boolean) => {
    setSelectionDirtied(true) // Enables save button
    onFormStateChange(true) // Alert parent that form is dirty
    setRbacSettingsForRole(prevRows => rowUpdateFunction(prevRows, id, checked))
  }

  const columns = useMemo(() => {
    return [
      {
        flex: 0.8,
        field: 'allow',
        headerName: 'Access Allowed',
        sortable: false,
        renderCell: params => (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flex: 0.7,
            }}
          >
            <Checkbox
              checked={params.row.allow}
              onChange={e => handleCheckboxChange(params.row.id, e.target.checked)}
              indeterminate={params.row.indeterminate || false}
            />
          </Box>
        ),
      },
    ]
  }, [])

  //? hydrate the grid with the settings data from backend
  useEffect(() => {
    const allPersistedRbacSettings = settingsData?.adminSettings?.rbac
    const persistedSettingsForRole = settingsData?.adminSettings?.rbac?.[roleId]
    if (allPersistedRbacSettings) {
      setAllRbacSettings(allPersistedRbacSettings)
      setRbacSettingsForRole(mapApiModelToGridModel(persistedSettingsForRole))
    } else {
      // set the default settings if there is no persisted settings
      const defaultPermissionsForRole = defaultAdminSettings.rbac[roleId]
      const hasDispatchFeatures = appRouteAssets && appRouteLoadBoard
      if (hasDispatchFeatures) {
        setRbacSettingsForRole(mapApiModelToGridModel(defaultPermissionsForRole))
      } else {
        // filter out the assets and load board permissions if yard doesn't have those features
        const filteredDefaultPermissionsForRole = defaultPermissionsForRole.filter(permission => {
          return !(permission.route == 'assets' || permission.route == 'load-board')
        })
        const filteredDefaultRbac: AdminRbacSettings = {
          yardManager: filteredDefaultPermissionsForRole,
          scaleOperator: filteredDefaultPermissionsForRole,
          yardEmployee: filteredDefaultPermissionsForRole,
          driver: filteredDefaultPermissionsForRole,
        }
        setRbacSettingsForRole(mapApiModelToGridModel(filteredDefaultPermissionsForRole))
        setAllRbacSettings(filteredDefaultRbac)
      }
    }
  }, [roleId, settingsData])

  const handleSave = async () => {
    const unFlattenedPermissions = mapGridModelToApiModel(rbacSettingsForRole)
    // patch in the changes made to the specific role
    const adminRbacSettings = {
      ...allRbacSettings,
      [roleId]: unFlattenedPermissions,
    }
    try {
      await postRbacSetting({ adminRbacSettings }).unwrap()
      handleSuccess()
    } catch (error) {
      handleError(error)
    }
  }

  const handleSuccess = () => {
    dispatch(
      setSnackBar({
        snackbarType: 'success',
        snackbarMessage: 'permissions updated successfully',
      }),
    )
    setSelectionDirtied(false) // reset local dirty state
    onFormStateChange(false) // alert parent form is clean
  }
  const handleError = error => {
    console.error(error)
    dispatch(
      setSnackBar({
        snackbarType: 'error',
        snackbarMessage: 'An error occurred while updating permissions',
      }),
    )
  }

  const BladeTitle = GS_ROLE_INFO[roleId].name + ' Role Configuration'

  return (
    <Stack height="100vh" justifyContent="flex-start">
      {/*Header*/}
      <FormModalHeading title={BladeTitle} backButtonPosition="right" onBack={() => onHideModal()} />
      <Box px={2}>
        <Typography variant="body1" color="textSecondary" sx={{ mb: 3, padding: theme.spacing(2) }}>
          Select the Modules that this role should have access to.
        </Typography>
      </Box>

      <Divider />

      {/* GridContent - Make this scrollable */}
      <Box flexGrow={1} sx={{ overflowY: 'auto', maxHeight: 'calc(100vh - 200px)' }}>
        <BasicDataGridV2
          columns={columns}
          rows={rbacSettingsForRole}
          sx={getGridStyles(theme)}
          hideFooter
          disableSelectionOnClick
          rowHeight={40}
          loading={bladeDataLoading}
          treeData
          getTreeDataPath={row => row.path}
          groupingColDef={{
            headerName: 'Module',
            flex: 1,
          }}
        ></BasicDataGridV2>
      </Box>

      <Divider />

      {/* Footer */}
      <Stack
        direction="row"
        p={2}
        spacing={2}
        sx={{
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <Stack direction="row" spacing={2}>
          <Button variant="contained" disabled={!selectionDirtied} onClick={handleSave}>
            Save
          </Button>
          <Button variant="outlined" onClick={() => onHideModal()}>
            Cancel
          </Button>
        </Stack>
      </Stack>
    </Stack>
  )
}

export default ConfigureRoleBlade

const getGridStyles = theme => ({
  border: 'none',
  '& .MuiDataGrid-columnHeaderTitleContainer': {
    padding: theme.spacing(3),
  },
  '& .MuiDataGrid-columnSeparator': {
    visibility: 'hidden',
  },
  '& .MuiDataGrid-cell:focus': {
    outline: 'none',
  },
  '& .MuiDataGrid-columnHeader:focus': {
    outline: 'none',
  },
  '& .MuiDataGrid-cell:focus-within': {
    outline: 'none',
  },
})
